import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getAccessLogs } from '../api/client';

// Two independent cards, two independent requests - same "one slow/erroring
// source shouldn't block the other" reasoning as Transaction Pending's
// Java/PHP cards. Both sources are read directly from CloudWatch Logs by
// the BFF (accessLogs.js), not proxied through Lumen - neither log group
// belongs to this repo's own pipeline.
//
// Lazy-loading (2026-08-07): originally cursor-based Previous/Next (each
// page replaced the visible event list, with a small `tokensRef` stack of
// previously-seen tokens to support paging back). Replaced with the same
// accumulating-list pattern used in components/LogGroupCard.jsx (the
// "InstapayApp Logs" pages) - events grow across fetches instead of one
// page swapping for another, auto-loaded via an IntersectionObserver on a
// sentinel row scrolled into view within the card's own scrollable table,
// plus a "Load more" button as a manual fallback. `nextTokenRef` only needs
// to remember the single cursor for the next fetch now, not a full
// per-page history - there's no "page N" to go back to any more.

const STATUS_LABELS = {
  ok: null,
  unavailable: 'This log group is not configured for this environment.',
  error: 'CloudWatch Logs request failed.',
};

function formatManilaTime(ms) {
  if (!ms) {
    return '—';
  }
  return new Date(ms).toLocaleString('en-PH', {
    timeZone: 'Asia/Manila',
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function AccessLogCard({ title, hint, source }) {
  const [events, setEvents] = useState([]);
  const [startTime, setStartTime] = useState(null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [query, setQuery] = useState('');

  // The CloudWatch pagination cursor for the NEXT fetch - a ref, not state,
  // since it's only ever read at the moment a fetch happens, never rendered.
  const nextTokenRef = useRef(undefined);
  // Guards against two overlapping fetches (e.g. the IntersectionObserver
  // firing again before the in-flight request resolves).
  const loadingRef = useRef(false);
  const sentinelRef = useRef(null);

  const fetchPage = useCallback(
    async (reset) => {
      if (loadingRef.current) {
        return;
      }
      loadingRef.current = true;
      if (reset) {
        setLoadingInitial(true);
        setError(null);
      } else {
        setLoadingMore(true);
      }
      try {
        const token = reset ? undefined : nextTokenRef.current;
        const data = await getAccessLogs(source, token);
        setEvents((prev) => (reset ? data.events || [] : [...prev, ...(data.events || [])]));
        setStartTime(data.start_time);
        setStatus(data.status);
        setHasMore(Boolean(data.has_more));
        nextTokenRef.current = data.next_token || undefined;
        setError(null);
      } catch (err) {
        setError(err.message || 'Failed to load logs.');
      } finally {
        setLoadingInitial(false);
        setLoadingMore(false);
        loadingRef.current = false;
      }
    },
    [source]
  );

  useEffect(() => {
    nextTokenRef.current = undefined;
    setEvents([]);
    setHasMore(false);
    fetchPage(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source]);

  function handleRefresh() {
    nextTokenRef.current = undefined;
    setEvents([]);
    setHasMore(false);
    fetchPage(true);
  }

  // Auto-load the next page once the sentinel row scrolls into view within
  // this card's own scrollable table container (root) - not the whole
  // browser viewport, which would trigger a load the moment the card itself
  // is on screen.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore) {
      return undefined;
    }
    const root = el.closest('.table-responsive') || null;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !loadingRef.current) {
          fetchPage(false);
        }
      },
      { root, rootMargin: '200px', threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, fetchPage]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return events;
    }
    return events.filter((e) => (e.message || '').toLowerCase().includes(q));
  }, [events, query]);

  const statusNote = status ? STATUS_LABELS[status] : null;

  return (
    <div className="col-lg-6">
      <div className="card border-0 shadow-sm h-100">
        <div className="card-body d-flex flex-column">
          <div className="d-flex align-items-center justify-content-between mb-1">
            <h2 className="h6 mb-0">{title}</h2>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={handleRefresh}
              disabled={loadingInitial}
            >
              {loadingInitial ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
          <p className="text-muted small mb-2">{hint}</p>
          <p className="text-muted small mb-3">
            {startTime && <>Today from {formatManilaTime(startTime)} (Asia/Manila)</>}
          </p>

          {error && <div className="alert alert-danger py-2 small">{error}</div>}
          {!error && statusNote && <div className="alert alert-warning py-2 small">{statusNote}</div>}

          {!error && status !== 'unavailable' && (
            <input
              type="text"
              className="form-control form-control-sm mb-2"
              placeholder="Filter loaded logs…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          )}

          {loadingInitial && events.length === 0 && !error ? (
            <div className="d-flex align-items-center gap-2 text-muted py-3">
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
              Loading…
            </div>
          ) : (
            !error &&
            status !== 'unavailable' && (
              <div className="table-responsive" style={{ maxHeight: '28rem', overflowY: 'auto' }}>
                <table className="table table-sm align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: '11rem' }}>Time (Manila)</th>
                      <th>Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((e, idx) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <tr key={`${e.timestamp}-${idx}`}>
                        <td className="text-nowrap text-muted">{formatManilaTime(e.timestamp)}</td>
                        <td className="font-monospace text-break" style={{ fontSize: '0.75rem' }}>
                          {e.message}
                        </td>
                      </tr>
                    ))}
                    {filtered.length === 0 && !loadingInitial && (
                      <tr>
                        <td colSpan={2} className="text-center text-muted py-4">
                          {events.length === 0 ? 'No log events for today yet.' : 'No log lines match this filter.'}
                        </td>
                      </tr>
                    )}
                    {/* Lazy-load sentinel - only rendered once there's an
                        initial result set, so it never fires before the
                        first real page has loaded. */}
                    {events.length > 0 && (
                      <tr ref={sentinelRef}>
                        <td colSpan={2} className="text-center text-muted py-3">
                          {loadingMore ? (
                            <span className="d-flex align-items-center justify-content-center gap-2">
                              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                              Loading more…
                            </span>
                          ) : hasMore ? (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => fetchPage(false)}
                            >
                              Load more
                            </button>
                          ) : (
                            'End of today’s log lines.'
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )
          )}

          {!error && status !== 'unavailable' && events.length > 0 && (
            <p className="text-muted small mt-2 mb-0">
              {query.trim()
                ? `Found ${filtered.length} of ${events.length} loaded events`
                : `Showing ${events.length} loaded events`}
              {hasMore ? ' (more available)' : ''}.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AccessLogs() {
  return (
    <div>
      <h1 className="h4 mb-2">Access Logs</h1>

      <div className="row g-3">
        <AccessLogCard title="Instapay Router Access Log" source="router" />
        <AccessLogCard title="Instapay Direct Access Log" source="direct" />
      </div>
    </div>
  );
}
